import React from 'react';
import { Clock, Directions } from '../Icons/CustomIcons';
import { Advice } from '../Common/Card';
import DateTimeReportLine from './DateTimeReportLine';
import RecurringEventsDisplay from './RecurringEventsDisplay';
import { ValidityPeriodText } from './Dialog';

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAYS_EN_TO_JS = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6
};

/**
 * Berechnet die nächsten Termine für wiederkehrende Events
 */
const getNextRecurringDates = (eventSchedule, filterDate) => {
  if (!eventSchedule) return [];
  
  const dates = [];
  const { recurrence, day_of_week, start_time, end_time, additional_times, valid_from, valid_until } = eventSchedule;
  const filterDateObj = new Date(filterDate);
  filterDateObj.setHours(0, 0, 0, 0); // Normalisiere auf Mitternacht
  
  // Bestimme Start- und Enddatum, normalisiere auf Mitternacht
  const startDate = valid_from ? new Date(valid_from) : null;
  if (startDate) startDate.setHours(0, 0, 0, 0);
  const endDate = valid_until ? new Date(valid_until) : null;
  if (endDate) endDate.setHours(0, 0, 0, 0);
  
  // Wenn recurrence nicht definiert ist, behandle es als 'daily'
  const effectiveRecurrence = recurrence || 'daily';
  
  // Tägliche Wiederholung
  if (effectiveRecurrence === 'daily') {
    // Prüfe ob filterDate im Gültigkeitsbereich liegt
    const isInRange = (!startDate || filterDateObj >= startDate) && (!endDate || filterDateObj <= endDate);
    if (isInRange) {
      dates.push({
        date: new Date(filterDateObj),
        start_time,
        end_time,
        additional_times: additional_times || []
      });
    }
    return dates;
  }
  
  // Wöchentliche/Zweiwöchentliche/Dreiwöchentliche/Vierwöchentliche Wiederholung
  if (day_of_week && (effectiveRecurrence === 'weekly' || effectiveRecurrence === 'biweekly' || 
                       effectiveRecurrence === 'triweekly' || effectiveRecurrence === 'fourweekly')) {
    const targetDay = DAYS_EN_TO_JS[day_of_week];
    const filterDay = filterDateObj.getDay();
    
    // Prüfe ob filterDate auf den richtigen Wochentag fällt
    if (filterDay === targetDay) {
      // Prüfe ob filterDate im Gültigkeitsbereich liegt
      const isInRange = (!startDate || filterDateObj >= startDate) && (!endDate || filterDateObj <= endDate);
      
      if (isInRange) {
        // Bei wöchentlich: immer hinzufügen
        if (effectiveRecurrence === 'weekly') {
          dates.push({
            date: new Date(filterDateObj),
            start_time,
            end_time,
            additional_times: additional_times || []
          });
        } 
        // Bei mehrwöchentlich: prüfe ob es die richtige Woche ist
        else if (startDate) {
          const intervalWeeks = {
            'biweekly': 2,
            'triweekly': 3,
            'fourweekly': 4
          }[effectiveRecurrence];
          
          // Berechne Wochen-Differenz seit Start
          const daysDiff = Math.floor((filterDateObj - startDate) / (1000 * 60 * 60 * 24));
          const weeksDiff = Math.floor(daysDiff / 7);
          
          if (weeksDiff % intervalWeeks === 0) {
            dates.push({
              date: new Date(filterDateObj),
              start_time,
              end_time,
              additional_times: additional_times || []
            });
          }
        }
      }
    }
    return dates;
  }
  
  // Monatliche Wiederholung
  if (day_of_week && effectiveRecurrence.startsWith('monthly_')) {
    const targetDay = DAYS_EN_TO_JS[day_of_week];
    const occurrence = effectiveRecurrence.split('_')[1]; // first, second, third, fourth, last
    
    // Berechne alle möglichen Termine für diesen Monat
    const monthDates = findMonthlyOccurrence(filterDateObj, targetDay, occurrence);
    
    // Prüfe ob filterDate mit einem der berechneten Daten übereinstimmt
    for (const monthDate of monthDates) {
      if (filterDateObj.getFullYear() === monthDate.getFullYear() &&
          filterDateObj.getMonth() === monthDate.getMonth() &&
          filterDateObj.getDate() === monthDate.getDate()) {
        // Prüfe ob im Gültigkeitsbereich
        const isInRange = (!startDate || monthDate >= startDate) && (!endDate || monthDate <= endDate);
        if (isInRange) {
          dates.push({
            date: new Date(monthDate),
            start_time,
            end_time,
            additional_times: additional_times || []
          });
          break; // Nur ein Termin pro filterDate
        }
      }
    }
    return dates;
  }
  
  return dates;
};

/**
 * Findet alle Vorkommen eines Wochentags in einem Monat oder den letzten
 * @returns Array von Date-Objekten
 */
const findMonthlyOccurrence = (date, targetDay, occurrence) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  
  if (occurrence === 'last') {
    // Letzter Wochentag des Monats
    const lastDay = new Date(year, month + 1, 0);
    const lastDayOfWeek = lastDay.getDay();
    const diff = (lastDayOfWeek - targetDay + 7) % 7;
    const result = new Date(year, month, lastDay.getDate() - diff);
    result.setHours(0, 0, 0, 0);
    return [result];
  }
  
  // Finde alle Vorkommen des Wochentags im Monat
  const dates = [];
  const firstDay = new Date(year, month, 1);
  const firstDayOfWeek = firstDay.getDay();
  const daysUntilTarget = (targetDay - firstDayOfWeek + 7) % 7;
  
  // Starte mit dem ersten Vorkommen
  let currentDate = 1 + daysUntilTarget;
  
  // Sammle alle Vorkommen (maximal 5)
  while (currentDate <= 31) {
    const testDate = new Date(year, month, currentDate);
    // Prüfe ob das Datum noch im gleichen Monat liegt
    if (testDate.getMonth() !== month) break;
    const resultDate = new Date(year, month, currentDate);
    resultDate.setHours(0, 0, 0, 0);
    dates.push(resultDate);
    currentDate += 7; // nächste Woche
  }
  
  return dates;
};

/**
 * Rendert alle Zeitslots für einen dateInfo-Eintrag.
 * activeTimeStart wird nicht zum Filtern genutzt – alle Zeiten werden immer angezeigt.
 */
const renderTimeSlots = (dateInfo) => {
  const allTimes = [];
  if (dateInfo.start_time) allTimes.push({ start_time: dateInfo.start_time, end_time: dateInfo.end_time });
  (dateInfo.additional_times || []).forEach(tp => allTimes.push(tp));

  if (allTimes.length === 0) return null;
  return allTimes.map((tp, i) => (
    <div key={i} className="ac-datetime-schedules">
      <Clock size={24} />
      <span className="body-m body--semibold line-height-default letter-spacing-tight">
        {tp.start_time}{tp.end_time && ` – ${tp.end_time}`} Uhr
      </span>
    </div>
  ));
};

/**
 * Gemeinsame Darstellungslogik für single_event und multi_day.
 * Gibt { upcomingDates, displayText } zurück.
 */
const buildEventDatesDisplay = (eventSchedule, showAll, filterDate) => {
  const eventDates = eventSchedule.dates || (eventSchedule.date ? [{
    date: eventSchedule.date, start_time: eventSchedule.start_time, end_time: eventSchedule.end_time
  }] : []);
  let dates = eventDates.map(ed => ({ date: new Date(ed.date), start_time: ed.start_time, end_time: ed.end_time, additional_times: ed.additional_times || [] }));

  if (showAll) {
    return {
      upcomingDates: dates,
      displayText: (
        <div className="opening-hours-list">
          {dates.map((dateInfo, index) => (
            <DateTimeReportLine
              key={index}
              label={new Date(dateInfo.date).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
              dayData={dateInfo.start_time ? { opens: dateInfo.start_time, closes: dateInfo.end_time || '' } : null}
              onAdd={() => {}}
              onRemove={() => {}}
              onUpdate={() => {}}
            />
          ))}
        </div>
      ),
    };
  }

  const filterDateObj = new Date(filterDate); filterDateObj.setHours(0, 0, 0, 0);
  dates = dates.filter(dateInfo => {
    const d = new Date(dateInfo.date); d.setHours(0, 0, 0, 0);
    return d.getFullYear() === filterDateObj.getFullYear() && d.getMonth() === filterDateObj.getMonth() && d.getDate() === filterDateObj.getDate();
  });
  return { upcomingDates: dates, displayText: null };
};

const EventScheduleDisplay = ({ eventType, openingHours, eventSchedule, accessibleFrom, accessibleUntil, website, filterDate, activeTimeStart, headerAction }) => {
  let upcomingDates = [];
  let displayText = null;

  const renderWebsiteButton = () => website && (
    <div className="ac-website">
      <a
        href={website}
        target="_blank"
        rel="noopener noreferrer"
        className="activity-detail-action-btn activity-detail-action-btn--maps"
      >
        <Directions size={26} /> Zur Webseite
      </a>
    </div>
  );

  const showAll = !filterDate; // Kontroll-Ansicht: kein Datum-Filter

  // Frei zugängliche Events
  if (eventType === 'freely_accessible') {
    const showInfo = showAll || (() => {
      const filterDateObj = new Date(filterDate); filterDateObj.setHours(0,0,0,0);
      const af = accessibleFrom ? (() => { const d = new Date(accessibleFrom); d.setHours(0,0,0,0); return d; })() : null;
      const au = accessibleUntil ? (() => { const d = new Date(accessibleUntil); d.setHours(0,0,0,0); return d; })() : null;
      return (!af || filterDateObj >= af) && (!au || filterDateObj <= au);
    })();

    if (showInfo) {
      displayText = (
        <div style={{ padding: 'var(--spacing-3)', backgroundColor: 'white', borderRadius: 'var(--radius-sm)' }}>
          <p style={{ fontWeight: '500', margin: 'var(--spacing-1) 0' }}>Frei zugänglich</p>
          <ValidityPeriodText validFrom={accessibleFrom} validUntil={accessibleUntil} freely_accessible/>
        </div>
      );
    }
  }
  // Einzeltermin(e) / Mehrtägiger Event
  else if ((eventType === 'single_event' || eventType === 'multi_day') && eventSchedule) {
    ({ upcomingDates, displayText } = buildEventDatesDisplay(eventSchedule, showAll || eventType === 'multi_day', filterDate));
  }
  // Wiederkehrende Events
  else if (eventType === 'recurring_event' && eventSchedule) {
    if (showAll) {
      displayText = <RecurringEventsDisplay eventSchedule={eventSchedule} />;
    } else {
      upcomingDates = getNextRecurringDates(eventSchedule, filterDate);
    }
  }
  // Reguläre Öffnungszeiten
  else if (eventType === 'regular_hours' && openingHours) {
    if (showAll) {
      const DAYS_DE = { monday: 'Montag', tuesday: 'Dienstag', wednesday: 'Mittwoch', thursday: 'Donnerstag', friday: 'Freitag', saturday: 'Samstag', sunday: 'Sonntag' };
      displayText = (
        <div className="opening-hours-list">
          {DAYS_ORDER.filter(day => openingHours.some(h => h.day === day)).map(day => (
            <DateTimeReportLine
              key={day}
              label={DAYS_DE[day]}
              dayData={openingHours.find(h => h.day === day) || null}
              onAdd={() => {}}
              onRemove={() => {}}
              onUpdate={() => {}}
            />
          ))}
        </div>
      );
    } else {
      const filterDateObj = new Date(filterDate); filterDateObj.setHours(0,0,0,0);
      const jsDay = filterDateObj.getDay();
      const dayName = jsDay === 0 ? 'sunday' : DAYS_ORDER[jsDay - 1];
      const hours = openingHours.find(h => h.day === dayName);
      if (hours && hours.opens) {
        upcomingDates = [{ date: filterDateObj, start_time: hours.opens, end_time: hours.closes || null, additional_times: [] }];
      }
    }
  }

  return (
    <div className="event-schedule-display">
      {/* Header mit Titel und optionaler Beschreibung */}
      <div className="event-schedule-header-row">
        <div className="event-schedule-header">
          <h2 className="card-header">Termine</h2>
          <p className="card-subtitle">
            {filterDate
              ? eventType === 'multi_day'
                  ? `Hier sehen Sie alle Termine der Aktivität, die am ${new Date(filterDate).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })} beginnt.`
                  : `Hier sehen Sie alle Termine vom ${new Date(filterDate).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}.`
              : eventType === 'multi_day'
                  ? 'Hier sehen Sie alle Termine für diese Aktivität.'
                  : 'Hier sehen Sie alle verfügbaren Termine.'}
          </p>
        </div>
        <div className="ac-website-desktop">{renderWebsiteButton()}</div>
      </div>

      {/* Wenn displayText gesetzt ist (z.B. für frei zugängliche Events oder Zusammenfassung von Wiederholungsregeln), zeige das anstelle der Terminliste */}
      {displayText || (upcomingDates.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
          {upcomingDates.map((dateInfo, index) => (
            <div key={index} className="ac-datetime-list">
              {showAll && (
                <p style={{ fontWeight: 500, margin: '0 0 var(--spacing-1)' }}>
                  {new Date(dateInfo.date).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              )}
              {renderTimeSlots(dateInfo)}
            </div>
          ))}
        </div>
      ) : (
        <div className="ac-datetime-list">
          <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>Keine Termine verfügbar</p>
        </div>
      ))}

      <div className="ac-website-mobile">{renderWebsiteButton()}</div>

      <Advice text="Terminangaben ohne Gewähr. Bitte überprüfen Sie die Seite der Aktivität." />

      {headerAction && <div>{headerAction}</div>}
    </div>
  );
};

export default EventScheduleDisplay;
